import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiUser, FiCalendar, FiLogOut, FiChevronDown, FiSettings, FiGrid } from 'react-icons/fi';
import { Button } from './common';
import { logOut } from '../firebase/auth';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAdmin, isManager } = useAuth();
  const { company, companyName, companyLogo, isMainSite } = useTenant();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logOut();
    setIsDropdownOpen(false);
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Determine displayed name and logo
  const displayName = company ? companyName : 'CarBooking';
  const displayTagline = company ? company.location : 'Drive Your Dreams';

  return (
    <header className="bg-white/80 backdrop-blur-md shadow-soft sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            {companyLogo ? (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl overflow-hidden shadow-soft">
                <img src={companyLogo} alt={displayName} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-primary rounded-xl flex items-center justify-center shadow-soft">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
            )}
            <div>
              <h1 className="text-base sm:text-xl font-bold text-gray-900">{displayName}</h1>
              <p className="text-xs text-gray-500 hidden sm:block">{displayTagline}</p>
            </div>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {user ? (
              <>
                <Link to="/bookings">
                  <Button variant="outline" className="flex items-center gap-2">
                    <FiCalendar className="w-5 h-5" />
                    <span className="hidden md:inline">Bookings</span>
                  </Button>
                </Link>
                
                {/* Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {userProfile?.photoURL || user?.photoURL ? (
                        <img 
                          src={userProfile?.photoURL || user?.photoURL} 
                          alt={userProfile?.displayName || user?.displayName || 'User'} 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(userProfile?.displayName || user?.displayName)
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700">
                      {userProfile?.displayName || user?.displayName || 'User'}
                    </span>
                    <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">
                          {userProfile?.displayName || user?.displayName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        {(isAdmin || isManager) && (
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {isAdmin ? 'Admin' : 'Manager'}
                          </span>
                        )}
                      </div>

                      {/* Admin/Manager Links */}
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-purple-600 hover:bg-purple-50 transition-colors"
                        >
                          <FiGrid className="w-4 h-4" />
                          <span>Admin Dashboard</span>
                        </Link>
                      )}
                      
                      {isManager && !isAdmin && (
                        <Link
                          to="/manager"
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <FiSettings className="w-4 h-4" />
                          <span>Manager Dashboard</span>
                        </Link>
                      )}

                      {/* Menu Items */}
                      <Link
                        to="/profile"
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <FiUser className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <FiLogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link to="/login" className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary-600 transition-colors">
                <FiUser className="w-5 h-5" />
                <span className="font-medium">Sign In</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
